import React, {
  PropsWithChildren,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Loader } from './Loader';
import { getDimensions } from '@/utils';
import { useAppContext } from '@/context/AppContext';
import { ScrollRestoration } from 'react-router-dom';

interface IProps {
  /**
   * className for the `<main>` element
   */
  className?: string;
  /**
   * className for the root element
   */
  rootClassName?: string;
  /**
   * props for the child container
   */
  containerProps?: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >;
  /**
   * props for the header/navbar component container
   */
  headerProps?: React.ComponentProps<typeof Header>;
  /**
   * props for the footer component container
   */
  footerProps?: React.ComponentProps<typeof Footer>;
  /**
   * props for the loader component
   */
  loaderProps?: React.ComponentProps<typeof Loader>;
  /**
   * to remove the default footer from DOM
   */
  hideFooter?: boolean;
  /**
   * to remove the default header (navbar + page title and back navigation) from DOM
   */
  hideHeader?: boolean;
  /**
   * to make the main container take full width (1120px)
   */
  fullWidth?: boolean;
  /**
   * to center the main container and have a max-width of 800px
   */
  centered?: boolean;
  /**
   * to only show a loader in the layout
   */
  loading?: boolean;
  /**
   * to show an error state in the layout
   */
  hasError?: boolean;
  /**
   * To change title shown in the browser tab i.e document's title
   */
  tabTitle?: string;
  /**
   * to reset scroll position on page navigation
   */
  resetScroll?: boolean;
}

export type TDimensions = {
  width: number;
  height: number;
  elem?: HTMLElement | null;
};

interface IRenderPropValues {
  headerDimensions: TDimensions;
  footerDimensions: TDimensions;
  pageHeaderDimensions: TDimensions;
  mainContainerDimensions: TDimensions;
  gap: number;
  getDimensions: (id: string) => TDimensions;
  bg?: string;
}

const Main = ({
  children,
  containerProps,
  headerProps,
  footerProps,
  hideFooter,
  hideHeader,
  fullWidth,
  className,
  rootClassName,
  loading,
  loaderProps,
  hasError,
  centered,
  tabTitle,
  resetScroll,
}: IProps & {
  children?:
    | PropsWithChildren['children']
    | ((values: IRenderPropValues) => JSX.Element);
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerDimensions, setHeaderDimensions] = useState<TDimensions>({
    width: 0,
    height: 0,
  });

  const footerRef = useRef<HTMLDivElement>(null);
  const [footerDimensions, setFooterDimensions] = useState<TDimensions>({
    width: 0,
    height: 0,
  });

  const [pageHeaderDimensions, setPageHeaderDimensions] = useState<TDimensions>(
    {
      width: 0,
      height: 0,
    },
  );

  const [mainContainerDimensions, setMainContainerDimensions] =
    useState<TDimensions>({
      width: 0,
      height: 0,
    });

  const [areDimensionsPresent, setAreDimensionsPresent] = useState(
    hideFooter && hideHeader,
  );

  useLayoutEffect(() => {
    setHeaderDimensions(getDimensions('claims-header'));
    setPageHeaderDimensions(getDimensions('claims-page-header'));
    setFooterDimensions(getDimensions('claims-footer'));
    setMainContainerDimensions(getDimensions('claims-main'));
    setAreDimensionsPresent(true);
  }, []);

  const { isWebView } = useAppContext();

  useLayoutEffect(() => {
    if (isWebView) {
      document.documentElement.classList.add('no-scrollbar');
    }
  }, [isWebView]);

  return (
    <div
      // added min-h-100vh for fallback
      className={twMerge(
        'grid gap-8 min-h-[100vh] min-h-[100dvh]! bg-white lg:bg-secondary-50 font-body',
        rootClassName,
      )}
      style={{
        gridAutoRows: 'auto 1fr',
      }}
    >
      {tabTitle && <title>{tabTitle}</title>}
      {hideHeader ? null : (
        <Header
          bg="bg-white lg:bg-secondary-50"
          {...(headerProps ?? {})}
          fullWidth={fullWidth}
          centered={centered}
          ref={headerRef}
        />
      )}
      <main
        id="claims-main"
        className={twMerge(
          clsx({
            'px-5 lg:px-0 w-full mx-auto max-w-[100vw] md:max-w-[1120px]': true,
            [`pb-[${footerDimensions.height + 20}px]`]:
              areDimensionsPresent && footerDimensions.height > 0,
          }),
          className,
        )}
      >
        {resetScroll ? <ScrollRestoration /> : null}
        {loading ? (
          <Loader
            {...loaderProps}
            dimensions={{
              headerDimensions,
              footerDimensions,
              pageHeaderDimensions,
              gap: 32,
            }}
          />
        ) : (
          <div
            {...(containerProps ?? {})}
            className={twMerge(
              containerProps?.className,
              clsx({
                'w-full mx-auto': true,
                'max-w-[1024px] ml-auto mr-0':
                  fullWidth &&
                  !headerProps?.hideBack &&
                  !headerProps?.hidePageHeader,
                'max-w-[928px]': !fullWidth,
                'lg:ml-24': !fullWidth && !headerProps?.hideBack && !hideHeader,
                '!mx-auto': loading || hasError,
                '!max-w-[800px] !mx-auto': centered,
              }),
            )}
          >
            {typeof children === 'function'
              ? areDimensionsPresent
                ? children({
                    headerDimensions,
                    footerDimensions,
                    pageHeaderDimensions,
                    mainContainerDimensions,
                    gap: 32,
                    getDimensions,
                    bg: 'bg-white lg:bg-secondary-50',
                  })
                : null
              : children}
          </div>
        )}
      </main>
      {hideFooter ? null : <Footer {...(footerProps ?? {})} ref={footerRef} />}
    </div>
  );
};

const Layout = {
  Header,
  Main,
  Footer,
};

export default Layout;
